// [목적] 폴리오 시작 템플릿 선택 UI — 카드 그리드(FolioTemplatePicker) + 모달 래퍼(FolioTemplateDialog)
// [이유] /me/folios 빈 상태와 헤더 "템플릿으로 시작" 버튼이 동일한 선택 UI 를 공유하도록 분리했다.
//         데스크톱/모바일 두 레이아웃이 같은 파일 안에 중복 구현되어 있어 컴포넌트로 묶지 않으면 3곳이 갈라진다.
// [사이드 이팩트] 카드 클릭 시 onSelect(template) 호출 — 실제 생성/이동은 상위(useCreateFolioFromTemplate) 책임.
//                 pending 중에는 전체 그리드를 비활성화해 중복 생성(POST 2회) 을 막는다.
// [수정 시 고려사항] 색·간격·라운드는 packages/ui tokens 만 사용(CLAUDE §6-3).
//                   접근성: 카드는 ButtonBase(button) + aria-label, 아이콘은 aria-hidden, :focus-visible 아웃라인 유지(§6-4).

import {
  Box,
  ButtonBase,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import { tokens } from '@gangwoncanvas/ui';

import { FOLIO_TEMPLATES, type FolioTemplate } from '../../constants/folioTemplates.js';

const c = tokens.color;
const L = 'light' as const;
const INK_HI = c.ink.high[L];
const INK_MID = c.ink.mid[L];
const BORDER = c.line.base[L];
const BRAND = c.brand[500][L];
// 폴리오 화면은 편지지(paper) 톤 계열 — surface(쿨톤) 대신 paper[0] 사용
const SURFACE = c.paper[0][L];

interface FolioTemplatePickerProps {
  /** 템플릿 카드 클릭 핸들러 */
  onSelect: (template: FolioTemplate) => void;
  /** 생성 진행 중 여부 — true 면 전체 비활성화 */
  pending?: boolean;
  /** 진행 중인 템플릿 id — 해당 카드에만 스피너 표시 */
  pendingId?: string | null;
  /** 한 줄에 놓을 카드 수(기본 3 — 모바일은 1 권장) */
  columns?: number;
}

/**
 * [목적] 템플릿 카드 그리드 렌더
 * [이유] 빈 상태·모달 양쪽에서 동일 마크업 재사용
 * [사이드 이팩트] 없음(표시 전용) — 클릭은 onSelect 로 위임
 * [수정 시 고려사항] 템플릿 추가는 constants/folioTemplates.ts 만 수정하면 본 컴포넌트는 그대로 동작
 */
export function FolioTemplatePicker({
  onSelect,
  pending = false,
  pendingId = null,
  columns = 3,
}: FolioTemplatePickerProps) {
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: `repeat(${columns}, 1fr)`,
        gap: `${tokens.spacing[3]}px`,
        textAlign: 'left',
      }}
    >
      {FOLIO_TEMPLATES.map((t) => {
        const isPendingCard = pendingId === t.id;
        return (
          <ButtonBase
            key={t.id}
            onClick={() => onSelect(t)}
            disabled={pending}
            aria-label={`${t.label} 템플릿으로 폴리오 만들기`}
            sx={{
              display: 'block',
              textAlign: 'left',
              bgcolor: SURFACE,
              border: `1px solid ${BORDER}`,
              borderRadius: `${tokens.radius.m}px`,
              p: `${tokens.spacing[4]}px`,
              transition: `border-color ${tokens.motion.fast.duration}ms ${tokens.motion.fast.easing}, transform ${tokens.motion.fast.duration}ms ${tokens.motion.fast.easing}`,
              opacity: pending && !isPendingCard ? 0.5 : 1,
              '&:hover': { borderColor: BRAND, transform: 'translateY(-2px)' },
              '&:focus-visible': { outline: `2px solid ${BRAND}`, outlineOffset: 2 },
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: `${tokens.spacing[2]}px` }}>
              <Box component="span" aria-hidden="true" sx={{ fontSize: 15 }}>
                {t.icon}
              </Box>
              <Typography component="span" sx={{ fontSize: 13.5, fontWeight: 700, color: INK_HI }}>
                {t.label}
              </Typography>
              {isPendingCard && <CircularProgress size={13} sx={{ color: BRAND, ml: 'auto' }} />}
            </Box>
            <Typography
              sx={{ fontSize: 12, color: INK_MID, mt: `${tokens.spacing[1]}px`, lineHeight: 1.5 }}
            >
              {t.description}
            </Typography>
          </ButtonBase>
        );
      })}
    </Box>
  );
}

interface FolioTemplateDialogProps extends FolioTemplatePickerProps {
  open: boolean;
  onClose: () => void;
}

/**
 * [목적] 템플릿 선택 모달 — 폴리오가 이미 있는 사용자도 템플릿 생성에 접근할 수 있게 한다
 * [이유] 빈 상태 인라인 그리드만 두면 폴리오 1개 이상인 사용자에게 진입점이 사라진다.
 * [사이드 이팩트] Esc/백드롭 클릭으로 닫힘(MUI 기본) — 생성 중(pending)에는 닫기를 막는다.
 * [수정 시 고려사항] 모달 내부는 FolioTemplatePicker 를 그대로 사용 — 카드 UI 변경은 한 곳만 고치면 된다.
 */
export function FolioTemplateDialog({
  open,
  onClose,
  onSelect,
  pending = false,
  pendingId = null,
  columns = 3,
}: FolioTemplateDialogProps) {
  return (
    <Dialog
      open={open}
      onClose={() => {
        if (!pending) onClose();
      }}
      maxWidth="sm"
      fullWidth
      aria-labelledby="folio-template-dialog-title"
    >
      <DialogTitle
        id="folio-template-dialog-title"
        sx={{ fontSize: 16, fontWeight: 800, color: INK_HI }}
      >
        템플릿으로 시작
        <Typography
          sx={{ fontSize: 12.5, color: INK_MID, mt: `${tokens.spacing[1]}px`, fontWeight: 400 }}
        >
          고르면 바로 편집 화면으로 이동해요. 만들어진 폴리오는 비공개로 저장됩니다.
        </Typography>
      </DialogTitle>
      <DialogContent sx={{ pb: `${tokens.spacing[6]}px` }}>
        <FolioTemplatePicker
          onSelect={onSelect}
          pending={pending}
          pendingId={pendingId}
          columns={columns}
        />
      </DialogContent>
    </Dialog>
  );
}
